import React from "react";
import { Grid, Card, CardContent, CardMedia, Box, Avatar, Divider, Container } from "@mui/material";
import { Link } from "react-router-dom";
import MKTypography from "components/MKTypography";
import { getImageUrl, getAuthorAvatarUrl } from "utils/api/payloadCMS";
import useMediaQueries from "utils/mediaQueries.utils";

// Import existing styled components to maintain consistency
import {
  StyledBlogResultSectionContainer,
  StyledBlogResultInnerGrid,
  StyledBlogResultOuterCardBox,
  StyledBlogResultBottomOuterCardBox,
  StyledBlogResultBottomInnerCardBox,
  StyledBlogResultAvatarBox,
} from "./index.styles";

// Import the existing typography components for consistency
import { EllipsisHeaderTypography, EllipsisBodyTextTypography } from "pages/ScienceAndResearch/components/ClinicalResearch/index.styles";

const ImprovedBlogResults = ({ blogs, cssProps, headerType = "h2" }) => {
  const { isLg } = useMediaQueries();

  if (!blogs || blogs.length === 0) {
    return (
      <Container sx={{ textAlign: "center", py: 8 }}>
        <MKTypography variant="h6" color="text.secondary">
          No blog posts found matching your search.
        </MKTypography>
      </Container>
    );
  }

  return (
    <StyledBlogResultSectionContainer component={"section"} id={"blog-result-section"} cssProps={cssProps}>
      <Grid container spacing={2}>
        {blogs.map((blog) => {
          const featuredImageUrl = getImageUrl(blog.featuredImage);
          const authorAvatarUrl = getAuthorAvatarUrl(blog.author);

          const cleanSlug = blog.slug?.replace(/^\/+/, "");

          // Check if blog.slug contains a modality
          const hasModality =
            cleanSlug.includes("ketamine-therapy/blog/") ||
            cleanSlug.includes("psilocybin-therapy/blog/") ||
            cleanSlug.includes("online-therapy/blog/");

          const blogSlug = hasModality ? `/${cleanSlug}` : `/blog/${cleanSlug}`;

          return (
            <Grid item xs={12} md={6} lg={4} key={blog.id}>
              <StyledBlogResultInnerGrid component={Link} to={blogSlug} sx={{ textDecoration: "none" }}>
                <StyledBlogResultOuterCardBox component={Card} width={1} height={1}>
                  {featuredImageUrl && (
                    <CardMedia
                      image={featuredImageUrl}
                      title={blog.title}
                      alt={blog.featuredImage.alt !== '""' ? blog.featuredImage.alt : blog.title}
                      sx={{
                        minHeight: { xs: 250, md: 275 },
                        position: "relative",
                        margin: ".75rem",
                      }}
                    />
                  )}

                  <Box component={CardContent} position={"relative"} mt={4}>
                    <EllipsisHeaderTypography variant={"h6"} component={headerType} gutterBottom>
                      {blog.title}
                    </EllipsisHeaderTypography>
                    <EllipsisBodyTextTypography component="p" variant={isLg ? "body1" : "body2"} color="text.secondary">
                      {blog.description}
                    </EllipsisBodyTextTypography>

                    {/* Keywords/Tags - only show first 3 to avoid clutter */}
                    {blog.keywords && blog.keywords.length > 0 && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        {blog.keywords.slice(0, 3).map((keyword, index) => (
                          <span
                            key={index}
                            style={{
                              display: "inline-block",
                              background: "#f0f0f0",
                              padding: "3px 8px",
                              borderRadius: "12px",
                              fontSize: "0.7rem",
                              marginRight: "6px",
                              marginBottom: "4px",
                              color: "#666",
                            }}
                          >
                            {keyword}
                          </span>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box flexGrow={1} />

                  <StyledBlogResultBottomOuterCardBox>
                    <Box marginBottom={2}>
                      <Divider />
                    </Box>
                    <StyledBlogResultBottomInnerCardBox>
                      <StyledBlogResultAvatarBox>
                        <Avatar
                          aria-hidden="true"
                          src={authorAvatarUrl || "https://d3a0rs3velfrz.cloudfront.net/logos/almadelicButterFlyTransparentSm.webp"}
                          alt={"Image of Author"}
                          sx={{ marginRight: 1, height: "40px", width: "40px" }}
                        />
                        <MKTypography color={"text.secondary"}>{blog.author?.name || "Almadelic"}</MKTypography>
                      </StyledBlogResultAvatarBox>
                      <MKTypography color={"text.secondary"}>{blog.formattedDate}</MKTypography>
                    </StyledBlogResultBottomInnerCardBox>
                  </StyledBlogResultBottomOuterCardBox>
                </StyledBlogResultOuterCardBox>
              </StyledBlogResultInnerGrid>
            </Grid>
          );
        })}
      </Grid>
    </StyledBlogResultSectionContainer>
  );
};

export default ImprovedBlogResults;
